import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { FirebaseProduct, CreateProductData, ProductImage } from '../types/product';
import { uploadToCloudinary, deleteFromCloudinary } from './cloudinaryService';

interface ProductQueryOptions {
  category?: string;
  featured?: boolean;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  lastDoc?: any;
  search?: string;
  slug?: string;
}

class ProductService {
  private productsCollection = collection(db, 'products');

  async createProduct(productData: CreateProductData): Promise<string> {
    try {
      const dataWithTimestamps = {
        ...productData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const docRef = await addDoc(this.productsCollection, dataWithTimestamps);
      return docRef.id;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  async getProduct(id: string): Promise<FirebaseProduct | null> {
    try {
      const docRef = doc(this.productsCollection, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        } as FirebaseProduct;
      }

      return null;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  async getProducts(options: ProductQueryOptions = {}): Promise<FirebaseProduct[]> {
    try {
      const constraints: QueryConstraint[] = [];

      if (options.category) {
        constraints.push(where('category', '==', options.category));
      }

      if (options.featured !== undefined) {
        constraints.push(where('featured', '==', options.featured));
      }

      if (options.minPrice !== undefined) {
        constraints.push(where('price', '>=', options.minPrice));
      }

      if (options.maxPrice !== undefined) {
        constraints.push(where('price', '<=', options.maxPrice));
      }

      if (options.sortBy) {
        constraints.push(orderBy(options.sortBy, options.sortOrder || 'asc'));
      }

      if (options.limit) {
        constraints.push(limit(options.limit));
      }

      if (options.lastDoc) {
        constraints.push(startAfter(options.lastDoc));
      }

      if (options.slug) {
        constraints.push(where('slug', '==', options.slug));
      }

      const q = query(this.productsCollection, ...constraints);
      const querySnapshot = await getDocs(q);

      const products = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as FirebaseProduct[];

      if (options.search) {
        const searchLower = options.search.toLowerCase();
        return products.filter(
          (product) =>
            product.name.toLowerCase().includes(searchLower) ||
            product.description.toLowerCase().includes(searchLower),
        );
      }

      return products;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  async updateProduct(id: string, data: Partial<FirebaseProduct>): Promise<void> {
    try {
      const docRef = doc(this.productsCollection, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  async deleteProduct(id: string): Promise<void> {
    try {
      const product = await this.getProduct(id);
      if (!product) {
        throw new Error('Product not found');
      }

      await this.deleteProductImages(product.images);
      await deleteDoc(doc(this.productsCollection, id));
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  async uploadProductImage(file: File): Promise<ProductImage> {
    try {
      const { url, public_id, asset_id } = await uploadToCloudinary(file);
      return {
        url,
        alt: file.name,
        public_id,
        asset_id,
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  async deleteProductImages(images: ProductImage[]): Promise<void> {
    try {
      const deletePromises = images.map((image) => {
        if (image.public_id) {
          return deleteFromCloudinary(image.public_id);
        }
        return Promise.resolve();
      });

      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting product images:', error);
      throw error;
    }
  }

  async getFeaturedProducts(limit: number = 8): Promise<FirebaseProduct[]> {
    return this.getProducts({
      featured: true,
      limit,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
  }

  async updateStock(id: string, quantity: number): Promise<void> {
    const product = await this.getProduct(id);
    if (!product) {
      throw new Error('Product not found');
    }

    const newStock = product.stock + quantity;
    if (newStock < 0) {
      throw new Error('Insufficient stock');
    }

    await this.updateProduct(id, { stock: newStock });
  }
}

export const productService = new ProductService();